import { FC, useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { SubmitHandler, useForm } from "react-hook-form";
import { ErrorMessage } from "@hookform/error-message";
import FormError, { errorClass } from "components/form/formError";
import FloatingField from "components/form/FloatingField/FloatingField";
import {
  AccountContext,
  ILoginErrorReturn,
  LOGIN_ERRORS,
} from "auth/accountProvider";
import { useNavigate } from "react-router-dom";
import Checkbox from "components/form/inputs/Checkbox";
import { useValidation } from "components/form/useValidation";
import InfoAlert from "components/alert/InfoAlert";
import { LanguageSwitcher } from "components/header/languageSwitcher/LanguageSwitcher";
import SendVerificationCode from "./SendVerificationCode";
import { useLogin } from "./useLogin";
import "./Login.scss";

type FormValues = {
  username: string;
  password: string;
};

const Login: FC = () => {
  const { t } = useTranslation();
  const { authenticate, needsMfaSmsCode, isAuthenticated } =
    useContext(AccountContext);
  const rules = useValidation();
  const navigate = useNavigate();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<FormValues>();

  const [cognitoError, setCognitoError] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isPasswordVisible, setIsPasswordVisible] = useState<boolean>(false);

  const {
    handleSaveUsername,
    savedUsername,
    shouldSaveUsername,
    setShouldSaveUsername,
    handleRedirect,
    locationState,
  } = useLogin();

  /*
   * remove loading spinner if user go Back (using Back button)
   * from SendVerificationCode view to Login
   */
  useEffect(() => {
    setIsLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsMfaSmsCode]);

  useEffect(() => {
    if (isAuthenticated) {
      handleRedirect();
    }
  }, [isAuthenticated, handleRedirect]);

  const handleLogin: SubmitHandler<FormValues> = (form) => {
    setIsLoading(true);
    const username = form.username.toLowerCase();

    authenticate(username, form.password)
      .then(() => {
        setIsLoading(false);
        handleSaveUsername(username);
      })
      .catch((error: ILoginErrorReturn) => {
        if (error.type === LOGIN_ERRORS.NEW_PASSWORD_REQUIRED) {
          navigate("/reset-password", {
            state: {
              resetUsername: error.user?.getUsername(),
              forceReset: true,
              user: error.user,
            },
          });
        } else if (error.message) {
          setIsLoading(false);
          setCognitoError(
            t(error.message, t("Login unknown untranslated error"))
          );
        }
      });
  };

  return (
    <>
      {locationState?.isResetPassword && (
        <InfoAlert text={t("new password explanation")} />
      )}

      {locationState?.isSendCodeUsernameError && (
        <InfoAlert text={t("username not found explanation")} />
      )}

      {locationState?.isSessionTimeout && !needsMfaSmsCode && (
        <InfoAlert text={t("session timeout explanation")} />
      )}

      {needsMfaSmsCode ? (
        <SendVerificationCode />
      ) : (
        <form onSubmit={handleSubmit(handleLogin)}>
          <fieldset disabled={isLoading}>
            <div
              className={`form-group required ${errorClass(
                errors,
                "username"
              )}`}
            >
              <FloatingField
                id="input-username"
                data-testid="input-username"
                type="text"
                label={t("username")}
                defaultValue={savedUsername}
                hasError={!!errors.username}
                {...register("username", { ...rules.required, ...rules.email })}
              />
              <ErrorMessage {...{ errors }} name="username" as={FormError} />
            </div>

            <div
              className={`form-group required ${errorClass(
                errors,
                "password"
              )}`}
            >
              <FloatingField
                id="input-password"
                data-testid="input-password"
                type={isPasswordVisible ? "text" : "password"}
                label={t("password")}
                aria-label={t("password")}
                hasError={!!errors.password}
                trailing={
                  <button
                    id="password-toggler"
                    data-testid="password-toggler"
                    type="button"
                    className="password-toggler"
                    aria-label={t("toggle password visibility")}
                    onClick={() => {
                      setIsPasswordVisible(!isPasswordVisible);
                    }}
                  />
                }
                {...register("password", rules.required)}
              />
              <ErrorMessage {...{ errors }} name="password" as={FormError} />
            </div>

            <div className="form-group">
              <div className="row">
                {/* Forgot password link - temporarily hidden. To re-enable:
                    uncomment this block, change the checkbox col-12 below back
                    to col-6, and re-add `Link` to the react-router-dom import.
                <div className="col-6">
                  <div className="">
                    <Link to="/send-code" id="forgot-password">
                      {t("forgotPassword")}
                    </Link>
                  </div>
                </div>
                */}
                <div className="col-12">
                  <Checkbox
                    id="remember-username"
                    dataTestId="remember-username"
                    label={t("rememberUsername")}
                    checked={shouldSaveUsername}
                    onChange={() => {
                      setShouldSaveUsername(!shouldSaveUsername);
                    }}
                  />
                </div>
              </div>
            </div>

            <div className="form-group">
              {!isLoading && (
                <button
                  id="submit-btn"
                  type="submit"
                  className="btn btn-primary btn-login"
                  data-testid="submit-btn"
                >
                  {t("login")}
                </button>
              )}

              {isLoading && (
                <div className="d-flex justify-content-center">
                  <span className="spinner spinner-default-green" />
                </div>
              )}

              {!isLoading && cognitoError && (
                <FormError>{cognitoError}</FormError>
              )}
            </div>
          </fieldset>
        </form>
      )}

      <div className="mt-48 mb-16">
        <LanguageSwitcher isTitleVisible={false} />
      </div>
    </>
  );
};

export default Login;
